// ==================== VAT UTILITY FUNCTIONS ====================

/**
 * Calculate VAT - Core calculation logic
 * @param {number} amount - The amount to calculate
 * @param {number} vatRate - VAT rate percentage
 * @param {string} operation - 'add' or 'remove'
 * @returns {object} - { netAmount, vatAmount, grossAmount }
 */
export const calculateVAT = (amount, vatRate, operation) => {
  if (isNaN(amount) || amount <= 0) {
    return { netAmount: 0, vatAmount: 0, grossAmount: 0 };
  }
  
  let netAmount, vatAmount, grossAmount;
  
  if (operation === 'add') {
    // Add VAT: amount is NET
    netAmount = amount;
    vatAmount = netAmount * (vatRate / 100);
    grossAmount = netAmount + vatAmount;
  } else {
    // Remove VAT (scorporo): amount is GROSS
    grossAmount = amount;
    netAmount = grossAmount / (1 + vatRate / 100);
    vatAmount = grossAmount - netAmount;
  }
  
  return {
    netAmount: roundAmount(netAmount),
    vatAmount: roundAmount(vatAmount),
    grossAmount: roundAmount(grossAmount)
  };
};

/**
 * Round to specified decimal places
 */
export const roundAmount = (value, decimals = 2) => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Format currency with symbol
 */
export const formatCurrency = (amount, currency = 'EUR', locale = 'en') => {
  const localeMap = {
    en: 'en-GB',
    de: 'de-DE',
    fr: 'fr-FR',
    it: 'it-IT',
    es: 'es-ES'
  };
  
  try {
    return new Intl.NumberFormat(localeMap[locale] || 'en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (e) {
    // Fallback for unsupported currencies
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/**
 * Parse amount from string (handles different formats)
 */
export const parseAmount = (value) => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  
  // Remove currency symbols and spaces
  let cleaned = value.toString().replace(/[^\d.,\-]/g, '');
  
  // Handle European format (1.234,56) vs US format (1,234.56)
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  
  if (lastComma > lastDot) {
    // European format: 1.234,56
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // US format: 1,234.56
    cleaned = cleaned.replace(/,/g, '');
  }
  
  return parseFloat(cleaned) || 0;
};

/**
 * Calculate for multiple rates
 */
export const calculateMultiRate = (amount, rates, operation) => {
  return rates.map(rate => ({
    rate,
    ...calculateVAT(amount, rate, operation)
  }));
};

/**
 * Calculate invoice totals
 */
export const calculateInvoice = (items) => {
  let subtotalNet = 0;
  let totalVat = 0;
  const vatBreakdown = {};
  
  const itemResults = items.map(item => {
    const netTotal = item.quantity * item.unitPrice;
    const vatAmount = netTotal * (item.vatRate / 100);
    const grossTotal = netTotal + vatAmount;
    
    subtotalNet += netTotal;
    totalVat += vatAmount;
    
    const rateKey = `${item.vatRate}%`;
    vatBreakdown[rateKey] = (vatBreakdown[rateKey] || 0) + vatAmount;
    
    return {
      ...item,
      netTotal: roundAmount(netTotal),
      vatAmount: roundAmount(vatAmount),
      grossTotal: roundAmount(grossTotal)
    };
  });
  
  // Round breakdown values
  Object.keys(vatBreakdown).forEach(key => {
    vatBreakdown[key] = roundAmount(vatBreakdown[key]);
  });
  
  return {
    items: itemResults,
    subtotalNet: roundAmount(subtotalNet),
    vatBreakdown,
    totalVat: roundAmount(totalVat),
    totalGross: roundAmount(subtotalNet + totalVat)
  };
};

/**
 * Generate CSV from data
 */
export const generateCSV = (data, headers) => {
  const rows = [headers.join(',')];
  data.forEach(row => {
    rows.push(headers.map(h => row[h] ?? '').join(','));
  });
  return rows.join('\n');
};

/**
 * Download file
 */
export const downloadFile = (content, filename, type = 'text/csv') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Copy to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
};

/**
 * Local storage helpers
 */
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  },
  
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }
};

/**
 * History management
 */
export const historyManager = {
  KEY: 'vat_calc_history',
  MAX_ITEMS: 50,
  
  getAll: () => {
    return storage.get(historyManager.KEY, []);
  },
  
  add: (calculation) => {
    const history = historyManager.getAll();
    const newItem = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      ...calculation
    };
    history.unshift(newItem);
    if (history.length > historyManager.MAX_ITEMS) {
      history.pop();
    }
    storage.set(historyManager.KEY, history);
    // Emit event to notify other components
    window.dispatchEvent(new CustomEvent('vat-history-updated'));
    return newItem;
  },
  
  remove: (id) => {
    const history = historyManager.getAll();
    const filtered = history.filter(item => item.id !== id);
    storage.set(historyManager.KEY, filtered);
    // Emit event to notify other components
    window.dispatchEvent(new CustomEvent('vat-history-updated'));
    return filtered;
  },
  
  clear: () => {
    storage.remove(historyManager.KEY);
    // Emit event to notify other components
    window.dispatchEvent(new CustomEvent('vat-history-updated'));
    return [];
  }
};

/**
 * Settings management
 */
export const settingsManager = {
  KEY: 'vat_calc_settings',
  
  defaults: {
    language: 'en',
    theme: 'light',
    decimals: 2,
    defaultCountry: 'IT',
    showMultiRate: true
  },
  
  get: () => {
    return {
      ...settingsManager.defaults,
      ...storage.get(settingsManager.KEY, {})
    };
  },
  
  set: (settings) => {
    const current = settingsManager.get();
    const updated = { ...current, ...settings };
    storage.set(settingsManager.KEY, updated);
    return updated;
  },
  
  reset: () => {
    storage.set(settingsManager.KEY, settingsManager.defaults);
    return settingsManager.defaults;
  }
};
